/** Blocks of the learner home: goals (with the exam centre that receives the urgent exams), plans. */

import {
  type Learner,
  getAcademyMeRetrieveQueryKey,
  getAcademyUrgentListQueryKey,
  useAcademyMePartialUpdate,
} from "@agencyos/api-client";
import { Button, GlassCard, SelectField, SoftSlider, TextField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronRight, CreditCard, ShieldCheck, Target } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { fieldErrors } from "../lib/settings";
import { centerOptions, useCenters } from "./CenterPicker";
import { ChoosePlanPage } from "./sales/ChoosePlanPage";
import { HomeOffer } from "./sales/HomeOffer";
import { dateText } from "./shared";
import { CardTitle, SectionTitle } from "./ui";

const NCLC_TARGETS = [4, 5, 6, 7, 8, 9, 10];
const EDUCATION = ["secondary", "bac", "bachelor", "master", "doctorate", "other"];
const DEVICES = ["phone", "computer", "tablet"];

export function goalsMissing(me: Learner): boolean {
  return !me.exam_date && !me.education && !me.device;
}

export function GoalForm({ me, onDone }: { me: Learner; onDone?: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = useAcademyMePartialUpdate();
  const [target, setTarget] = useState(String(me.target_nclc ?? 7));
  const [examDate, setExamDate] = useState(me.exam_date ?? "");
  const [hours, setHours] = useState(me.hours_per_week ?? 5);
  const [education, setEducation] = useState(me.education ?? "");
  const [device, setDevice] = useState(me.device ?? "");
  const [center, setCenter] = useState(me.exam_center ?? "");
  const centers = useCenters();
  const errors = fieldErrors(save.error);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      {
        data: {
          target_nclc: Number(target),
          exam_date: examDate || null,
          hours_per_week: hours,
          education,
          device,
          exam_center: center,
        },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
          void queryClient.invalidateQueries({ queryKey: getAcademyUrgentListQueryKey() });
          onDone?.();
        },
      },
    );
  };

  return (
    <GlassCard as="section" className="ac-card ac-card--accent" aria-labelledby="academy-goals">
      <form className="ac-form" onSubmit={onSubmit}>
        <CardTitle id="academy-goals" icon={<Target size={16} />} tone="warning">
          {t("academy.goals.title")}
        </CardTitle>
        <p className="ac-secondary">{t("academy.goals.hint")}</p>
        <div className="ac-form-grid">
          <SelectField
            label={t("academy.goals.target")}
            hint={t("academy.goals.targetHint")}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            options={NCLC_TARGETS.map((n) => ({ value: String(n), label: t("academy.goals.nclc", { n }) }))}
            error={errors.target_nclc}
          />
          <TextField
            label={t("academy.goals.examDate")}
            type="date"
            value={examDate}
            onChange={(e) => setExamDate(e.target.value)}
            error={errors.exam_date}
          />
          <SelectField
            label={t("academy.center.label")}
            hint={center ? undefined : t("academy.center.hint")}
            value={center}
            onChange={(e) => setCenter(e.target.value)}
            options={centerOptions(centers, t("academy.goals.choose"), me.exam_center)}
            error={errors.exam_center}
          />
          <SelectField
            label={t("academy.goals.education")}
            value={education}
            onChange={(e) => setEducation(e.target.value)}
            options={[
              { value: "", label: t("academy.goals.choose") },
              ...EDUCATION.map((value) => ({ value, label: t(`academy.goals.educationOptions.${value}`) })),
            ]}
          />
          <SelectField
            label={t("academy.goals.device")}
            value={device}
            onChange={(e) => setDevice(e.target.value)}
            options={[
              { value: "", label: t("academy.goals.choose") },
              ...DEVICES.map((value) => ({ value, label: t(`academy.goals.deviceOptions.${value}`) })),
            ]}
          />
        </div>
        <SoftSlider
          label={t("academy.goals.hours")}
          min={1}
          max={40}
          value={hours}
          onChange={setHours}
          valueText={t("academy.goals.hoursValue", { count: hours })}
        />
        {save.isError && !Object.keys(errors).length && <p className="ko">{t("common.error")}</p>}
        <div className="ac-card__actions">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t("common.loading") : t("academy.goals.save")}
          </Button>
          {onDone && !goalsMissing(me) && (
            <Button variant="ghost" onClick={onDone}>
              {t("common.cancel")}
            </Button>
          )}
        </div>
      </form>
    </GlassCard>
  );
}

/** Current plan: active until, oral credits. */
function PlanStatus({ me }: { me: Learner }) {
  const { t, i18n } = useTranslation();
  return (
    <div className={`ac-plan-status${me.has_access ? " ac-plan-status--on" : ""}`}>
      <span className={`ac-pastille ac-tone--${me.has_access ? "success" : "accent"}`} aria-hidden="true">
        {me.has_access ? <ShieldCheck size={16} /> : <CreditCard size={16} />}
      </span>
      <div className="ac-plan-status__text">
        <p className={me.has_access ? "ac-plan-status__title" : "ac-secondary"}>
          {me.has_access
            ? me.active_until
              ? t("academy.plans.activeUntil", { date: dateText(me.active_until, i18n.language) })
              : t("academy.plans.active")
            : t("academy.plans.none")}
        </p>
        {me.eo_credits > 0 && <p className="ac-footnote">{t("academy.plans.eoCredits", { count: me.eo_credits })}</p>}
      </div>
    </div>
  );
}

/** The plan of the home. `compact`: status and a link (the learner already has a plan); otherwise
 * the Academy Pass and the free trial (D-110: one pass for the three exams). */
export function PlansSection({ me, compact = false }: { me: Learner; compact?: boolean }) {
  const { t } = useTranslation();
  if (!compact) return <HomeOffer />;
  return (
    <section className="ac-section" aria-labelledby="academy-plan">
      <SectionTitle
        id="academy-plan"
        action={
          <Link className="ac-more" to="/academy/plans">
            {t("academy.plans.extend")}
            <ChevronRight aria-hidden size={16} />
          </Link>
        }
      >
        {t("academy.plans.title")}
      </SectionTitle>
      <div className="ac-surface ac-card">
        <PlanStatus me={me} />
      </div>
    </section>
  );
}

/** /academy/plans: kept for older links; the page is « Choose my plan » (D-110). */
export function PlansPage() {
  return <ChoosePlanPage />;
}
